import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { Button } from '~/shadcn/ui/button/button'
import { Field, FieldError, FieldGroup, FieldLabel } from '~/shadcn/ui/field'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '~/shadcn/ui/input-otp'
import { Form, FormField, FormItem } from '~/shadcn/ui/form/form'
import { Spinner } from '~/shadcn/ui/spinner'
import { twoFaVerifySchema, type TwoFaVerifySchema } from './twoFaVerifySchema'
import { FormError } from '~/@mid_ui-form/form-error/FormError'
import { REGEXP_ONLY_DIGITS_AND_CHARS } from 'input-otp'

export type TwoFaVerifyFormState = {
  code: string
}

type Props = {
  handleSubmitForm: (props: TwoFaVerifyFormState) => void
  isRequesting: boolean
  error: string | null
  fieldLabel?: string
  codeLength?: number
  isDisabled?: boolean
  formContent?: React.ReactNode
  FooterComponent?: React.FC<{
    children: React.ReactNode
  }>
  submitButtonLabel?: string
  handleGoBack?: () => void
}

export function TwoFaVerifyForm({
  codeLength = 5,
  isDisabled,
  formContent,
  fieldLabel,
  FooterComponent,
  submitButtonLabel,
  handleSubmitForm,
  isRequesting,
  handleGoBack,
  error,
}: Props) {
  const form = useForm<TwoFaVerifySchema>({
    resolver: zodResolver(twoFaVerifySchema),
    defaultValues: {
      code: '',
    },
  })

  const actionButtons = (
    <div className="flex flex-row lg:flex-row justify-center items-center gap-2 w-full">
      {handleGoBack != null && (
        <Button
          variant="outline"
          type="button"
          onClick={handleGoBack}
          disabled={isRequesting || isDisabled}>
          Zrušiť
        </Button>
      )}
      <Button
        variant="accent"
        type="submit"
        size="xl"
        disabled={isRequesting || isDisabled}>
        {`${submitButtonLabel ?? 'Pokračovať'}`}{' '}
        {isRequesting && <Spinner className="size-4" />}
      </Button>
    </div>
  )

  const footer = (() => {
    if (FooterComponent)
      return <FooterComponent>{actionButtons}</FooterComponent>

    return (
      <FieldGroup>
        <Field>
          <div className="flex flex-col justify-center items-center mt-4 gap-4 w-full">
            {actionButtons}
          </div>
        </Field>
      </FieldGroup>
    )
  })()

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(({ code }) =>
          handleSubmitForm({ code: code.toUpperCase() })
        )}
        className="py-2 md:py-2">
        <FieldGroup className="gap-4">
          {formContent}

          <FormField
            control={form.control}
            name="code"
            render={({ fieldState, field }) => (
              <FormItem className="flex flex-col items-center my-4">
                {fieldLabel && (
                  <FieldLabel htmlFor="code">{fieldLabel}</FieldLabel>
                )}
                <InputOTP
                  id="code"
                  type="text"
                  autoComplete="one-time-code"
                  maxLength={codeLength}
                  inputMode="text"
                  pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
                  {...field}>
                  {new Array(codeLength).fill(0).map((_, index) => (
                    <InputOTPGroup key={`LoginTwoFaInputOtp-${index}`}>
                      <InputOTPSlot
                        className="font-semibold uppercase border-border"
                        index={index}
                      />
                    </InputOTPGroup>
                  ))}
                </InputOTP>
                {error ? (
                  <FormError error={error} />
                ) : (
                  fieldState.error && (
                    <FieldError>{fieldState.error.message}</FieldError>
                  )
                )}
              </FormItem>
            )}
          />

          {footer}
        </FieldGroup>
      </form>
    </Form>
  )
}
